import { useEffect, useMemo, useState, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Info, MessageSquare } from '@digital-twins/design-system/icons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { formatLabel } from '@/lib/utils/formatting'
import { Text } from '@/components/ui/Text'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { SubsegmentChip } from '@/components/twin-chat'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import {
  buildRespondentImageMapById,
  buildResponseImageMap,
  normalizePersonaMapKey,
} from '@/features/studies/report/audienceProfiles'
import { QuestionCard } from '../views/analysis/QuestionCard'
import { usePriceCurveBaseLine, usePriceCurvesLabels } from '../views/analysis/usePriceCurvesLabels'
import { useRankingBarsLabels } from '../views/analysis/useRankingBarsLabels'
import { findSurveyQuestion } from '@/features/studies/report/utils/findSurveyQuestion'
import { withoutPooledGridRows } from '@/features/studies/questionnaire/assets/liftTags'
import {
  normalizeQuestionAssetMentions,
  useReportQuestionCards,
} from '../views/overview/useReportQuestionCards'
import { resolvePairedQuestion } from '../views/overview/resolvePairedQuestion'
import { readTestedAssets } from '@/features/studies/report/utils/testedAssets'
import { SummaryItemSelector } from './SummaryItemSelector'
import { formatAbsoluteScore, formatKpiNumber, isShareUnit, shareIsFraction } from './formatAbsoluteScore'
import { FeaturedQuestionView, UnresolvedQuestionNotice } from './FeaturedQuestionView'
import { KpiListView } from './KpiListView'
import { LiftView } from './LiftView'
import { MetricFigureSource, resolveMetricFigure } from './resolveMetricFigure'
import { SummaryScorecard } from './SummaryScorecard'
import { PodiumPlotView, readPodiumCard, StoredPodiumCard } from '@digital-twins/deck-ui/plots/podium'
import { QuoteListPlot, readQuotesCard } from '@digital-twins/deck-ui/plots/quote-list'
import { QuoteListQuote, QuoteListSpeaker } from '@digital-twins/slide-schemas/generated/plot-types'
import { ChoicePlotView, donutAlternative, readDistributionCard, StoredDistributionCard } from '@digital-twins/deck-ui/plots/choice-bars'
import { RankingBarsPlot, readRankingCard, StoredRankingCard } from '@digital-twins/deck-ui/plots/ranking-bars'
import { PriceCurveChart } from '@digital-twins/deck-ui/components'
import {
  HeatGridPlotView,
  HeatGridPlotViewProps,
  readComparisonCard,
  StoredComparisonCard,
} from '@digital-twins/deck-ui/plots/heat-grid'
import { PriceCurvesPlot, summaryCardPriceCurveAdapter } from '@digital-twins/deck-ui/plots/price-curves'
import {
  cornerLabelsFromKeys,
  QuadrantPlot,
  readQuadrantCard,
  StoredQuadrantCard,
} from '@digital-twins/deck-ui/plots/quadrant'
import {
  CanonicalAnalysisOutput,
  CardShape,
  DistributionPayload,
  FallbackPayload,
  GridPayload,
  KpiListPayload,
  LiftPayload,
  PriceCurvePayload,
  QuadrantPayload,
  QuestionPayload,
  QuotesPayload,
  RankingPayload,
  RankingSeries,
  REPORT_CARD_SHAPE_CAPABILITIES,
  ScorecardPayload,
  SummaryCard,
  SummaryCardPayload,
  SummaryCardQuestion,
} from '@digital-twins/shared-types'
import { RankItem } from '@digital-twins/slide-schemas/generated/plot-types'
import { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'

export interface SummaryCardRenderContext {
  itemTags: Record<string, string>
  survey?: any
  responses?: Array<any>
  analysisColumns?: Array<string>
  segmentationMappings?: Array<SegmentationMapping> | null
  canonicalAnalysis?: CanonicalAnalysisOutput | null
  audienceProfiles?: Array<SummaryAudienceProfile>
  respondentCount?: number
  /** Opens the 1:1 chat of the respondent with this id (a quote speaker's `respondentId`). */
  onTwinChatClick?: (respondentId: string) => void
}

/** One audience profile the summary cards read (the rows carry `respondent_id`). */
export interface SummaryAudienceProfile {
  respondent_id?: string
  image_url?: string
  name?: string
}

interface RegistryViewProps {
  card: SummaryCard
  context: SummaryCardRenderContext
}

export const SUMMARY_CARD_RENDERER_BRANCHES = {
  kpi_rows: 'KpiListView',
  lift: 'LiftView',
  per_item_rank: 'RankingView',
  podium: 'RankingView',
  quadrant: 'QuadrantView',
  dim_rank: 'RankingView',
  distribution: 'DistributionView',
  price_curve: 'PriceCurveView',
  comparison: 'GridView',
  quotes: 'QuotesView',
  fallback: 'FallbackView',
  demand_curve: null,
  question: 'FeaturedQuestionView',
  scorecard: 'SummaryScorecard',
} as const satisfies Record<CardShape, string | null>

for (const shape of Object.keys(SUMMARY_CARD_RENDERER_BRANCHES) as CardShape[]) {
  if (
    SUMMARY_CARD_RENDERER_BRANCHES[shape] !==
    REPORT_CARD_SHAPE_CAPABILITIES[shape].renderer
  ) {
    throw new TypeError(`Summary-card renderer for ${shape} is out of contract.`)
  }
}

const AVATAR_VISIBLE = 5

function itemTag(itemTags: Record<string, string>, id: string, stamped?: string | null): string | undefined {
  if (stamped) return stamped
  return itemTags[id]
}

function formatDeltaValue(value: number, unit: string): string {
  const sign = value > 0 ? '+' : ''
  if (isShareUnit(unit)) {
    const pct = shareIsFraction(unit) ? value * 100 : value
    return `${sign}${formatKpiNumber(pct)}%`
  }
  return `${sign}${formatKpiNumber(value)}`
}

/**
 * The podium card: the podium plot (plots plan E) at its `card` size, the same
 * plot the podium slide draws. Every ranked item is on the board in the same
 * frame, pictures or not, and the escape rows are bars beneath it; Python
 * decides the pictures (each row its own, or the copy respondents read), the rank, the winner,
 * the tie and the decimals, and the card draws them. Tested assets ride the
 * board; text items (pain points, claims) draw as ranking bars (`itemKind`).
 * A card stored before those stamps reads through the legacy adapter and keeps
 * today's figures and today's board-or-bars rule.
 */
function PodiumCard(props: {
  payload: RankingPayload
  itemTags: Record<string, string>
  scoreLabel?: string | null
}) {
  const { payload, itemTags, scoreLabel } = props

  const { t } = useTranslation('app')

  const { payload: plot, isLegacy, layout } = readPodiumCard(payload as unknown as StoredPodiumCard)
  const storedById = new Map(payload.items.map((item) => [item.id, item]))
  const data = {
    ...plot.data,
    items: plot.data.items.map((item) => ({
      ...item,
      tag: item.isEscape ? null : (itemTag(itemTags, item.id, item.tag) ?? null),
    })),
    // The viewer's UI language (owner decision 5), not the study's.
    labels: {
      plot_rank_n: '#{rank}',
      too_close_to_call: t('studyReport.tooCloseToCall'),
      plot_lead_clear: t('studyReport.podiumLeadClear'),
      plot_lead_leads: t('studyReport.podiumLeadLeads'),
      plot_lead_slight: t('studyReport.podiumLeadSlight'),
      plot_small_base: t('studyReport.podiumSmallBase'),
    },
  }
  // Legacy only: a stored card prints the figure the way it always did.
  const formatLegacyValue = (item: { id: string }) => {
    const stored = storedById.get(item.id)
    return stored ? (legacyRankingFigure(stored, payload.unit) ?? String(stored.value)) : null
  }

  if (layout === 'bars') {
    // Text items: today's ranking bars, the heading beside the tie badge.
    return (
      <PodiumPlotView
        data={data}
        size="card"
        layout="bars"
        heading={scoreLabel}
        // Stamped names print as Python wrote them (Wave 7 S4); a stored card keeps today's recasing.
        formatItemLabel={isLegacy ? formatLabel : undefined}
        emptyLabel={t('studyReport.noRankingData')}
        formatValue={isLegacy ? formatLegacyValue : undefined}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {scoreLabel && (
        <span className="text-sm font-medium text-muted-foreground">{scoreLabel}</span>
      )}
      <PodiumPlotView
        data={data}
        size="card"
        tailAs="bars"
        crownLabel={data.labels.plot_rank_n.replace('{rank}', '1')}
        formatValue={isLegacy ? formatLegacyValue : undefined}
        testId="summary-podium-board"
      />
    </div>
  )
}

/** Exactly the `t` `useTranslation('app')` hands back, so no cast is needed. */
type Translate = ReturnType<typeof useTranslation<'app'>>['t']

/**
 * What the figure on a ranking row MEANS, named once for both variants.
 *
 * The analysis emits `scoreLabel` only when the figure is a composite score,
 * because a composite's label is authored study content ("Conversion Intent").
 * When the figure is the share of respondents who picked the item, the name of
 * that measure is UI copy in four locales, so it is named here off the
 * payload's own declared `basis` rather than invented from the numbers. A
 * `scored` podium and the two measure rankings keep whatever the payload says,
 * which for them is the only honest source.
 */
function rankingScoreLabel(
  payload: RankingPayload,
  t: Translate,
  series?: RankingSeries | null,
): string | undefined {
  // THE SELECTED SERIES FIRST, the payload only as the fallback. The
  // payload-level pair describes the DEFAULT series, so a `dim_rank` selector
  // printed "Trust" against the attention bars the moment the reader switched
  // dimensions. Read per field rather than per pair: a series that names its
  // measure without restating the machine unit still gets the payload's unit,
  // which is the only way that figure stays formatted.
  const declared = series?.scoreLabel ?? payload.scoreLabel
  if (declared) {
    return declared
  }
  if (payload.shape === 'podium' && payload.basis && payload.basis !== 'scored') {
    return t('studyReport.chosenBy')
  }
  return undefined
}

/**
 * A card stored before the plots plan I stamps prints its figure the way it
 * always did (owner decision 4): through `formatAbsoluteScore` whenever the
 * payload names a unit, else the stored `valueLabel`. A stamped card prints
 * Python's figure.
 */
function legacyRankingFigure(item: { value: number; valueLabel?: string | null }, unit?: string | null): string | undefined {
  if (unit) {
    return formatAbsoluteScore(item.value, unit)
  }
  return item.valueLabel ?? undefined
}

/**
 * `per_item_rank` / `dim_rank`: the ranking-bars plot (plots plan I) at its
 * card size, the same plot the ranking slide draws. Python stamps the
 * rank, the winner, the tie, the decimals and the scale; the card draws them
 * in the viewer's UI language. A card stored before those stamps reads through
 * the legacy adapter and keeps today's figures, rank and track.
 */
function RankingBarsCard(props: {
  payload: RankingPayload
  itemTags: Record<string, string>
  seriesId: string | null
  heading?: string | null
}) {
  const { payload, itemTags, seriesId, heading } = props

  const { t } = useTranslation('app')
  const labels = useRankingBarsLabels()

  const read = readRankingCard(payload as unknown as StoredRankingCard)
  const activeUnit = payload.series?.find((series) => series.id === seriesId)?.unit ?? payload.unit
  const formatValue = read.isLegacy && activeUnit
    ? (item: RankItem) => formatAbsoluteScore(item.value, activeUnit)
    : read.formatValue
  const withTags = (items: Array<RankItem>) =>
    items.map((item) => ({ ...item, tag: item.isEscape ? null : (itemTag(itemTags, item.id, item.tag) ?? null) }))
  const data = {
    ...read.payload.data,
    items: withTags(read.payload.data.items),
    series: read.payload.data.series?.map((series) => ({ ...series, items: withTags(series.items) })),
    // The viewer's UI language (owner decision 5); a legacy read keeps today's chrome only.
    labels: read.isLegacy ? { plot_rank_n: labels.plot_rank_n, too_close_to_call: labels.too_close_to_call } : labels,
  }

  return (
    <RankingBarsPlot
      data={data}
      size="card"
      highlight={read.highlight}
      seriesId={seriesId}
      heading={heading}
      formatValue={formatValue}
      // Stamped names print as Python wrote them (Wave 7 S4); a legacy read keeps today's recasing.
      formatItemLabel={read.isLegacy ? formatLabel : undefined}
      emptyLabel={t('studyReport.noRankingData')}
      testIdPrefix="summary-ranking-bars"
    />
  )
}

function RankingView(props: {
  payload: RankingPayload
  itemTags: Record<string, string>
}) {
  const { payload, itemTags } = props
  const { t } = useTranslation('app')
  const liveSeries = payload.series.filter((series) => series.items.length > 0)
  const showSelector = liveSeries.length >= 2
  const [selectedSeriesId, setSelectedSeriesId] = useState<string>(
    () => payload.defaultSeriesId ?? liveSeries[0]?.id ?? '',
  )
  const seriesKey = liveSeries.map((series) => series.id).join('|')

  useEffect(() => {
    setSelectedSeriesId((current) =>
      liveSeries.some((series) => series.id === current)
        ? current
        : (payload.defaultSeriesId ?? liveSeries[0]?.id ?? ''),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesKey, payload.defaultSeriesId])

  const activeSeries = useMemo(() => {
    if (!showSelector) {
      return null
    }
    return liveSeries.find((entry) => entry.id === selectedSeriesId) ?? null
  }, [showSelector, liveSeries, selectedSeriesId])

  const activeScoreLabel = rankingScoreLabel(payload, t, activeSeries)

  // The podium plot is the `podium` shape's only. `per_item_rank` /
  // `dim_rank` rank measures and dimensions rather than the tested set, so
  // neither turns into a board even when the study's items carry images.
  const showPodium = payload.shape === 'podium'

  return (
    <div className="flex flex-col gap-3">
      {showSelector && (
        <div className="flex justify-end">
          <SummaryItemSelector
            options={liveSeries.map((series) => ({ id: series.id, label: series.label }))}
            selected={selectedSeriesId}
            onChange={setSelectedSeriesId}
            label={t('studyReport.itemSelect')}
          />
        </div>
      )}
      {showPodium ? (
        // A podium ranks one set and carries no series, so the card reads `payload.items`; the selector's active series only feeds the other shapes.
        <PodiumCard payload={payload} itemTags={itemTags} scoreLabel={activeScoreLabel} />
      ) : (
        <RankingBarsCard payload={payload} itemTags={itemTags} seriesId={activeSeries?.id ?? null} heading={activeScoreLabel} />
      )}
    </div>
  )
}

function QuadrantView(props: { payload: QuadrantPayload; itemTags: Record<string, string> }) {
  const { payload, itemTags } = props

  const { t } = useTranslation('app')

  // Plots plan O: the card draws the `quadrant` kind Python stamped (plane,
  // midpoint, corners), or, for a card stored before it, the same payload read
  // off the legacy fields. Corner names come only from the recipe: its keys are
  // resolved here, in the viewer's UI language (owner decision 5); a stored
  // card's four strings print as stored.
  const plot = readQuadrantCard(payload as StoredQuadrantCard)
  const data = {
    ...plot.data,
    points: plot.data.points.map((point) => ({
      ...point,
      badge: itemTag(itemTags, point.id, point.badge) ?? null,
    })),
  }
  const cornerKeys = payload.cornerLabelKeys ?? null
  const cornerNames: Record<string, string> = cornerKeys
    ? t('studyReport.quadrantCorners', { returnObjects: true })
    : {}
  const cornerLabels = cornerKeys
    ? cornerLabelsFromKeys(cornerKeys, (key) => cornerNames[key])
    : (data.cornerLabels ?? null)

  return (
    // THE MAP NEEDS A HEIGHT OF ITS OWN. `QuadrantChart` is `w-full h-full`
    // and places every dot against that box; dropped into the Featured frame,
    // whose column is sized by its content, `h-full` resolved to zero and the
    // map collapsed behind its own axis labels. Clamped rather than fixed:
    // 320px is the smallest square the four corner labels still read in, and
    // past 420px the map outgrows the two cards beside it.
    //
    // NO LEGEND HERE, and not as a taste call: inside `QuadrantChart` the
    // legend is the only IN-FLOW child of a root whose plot area and axis
    // labels are all absolutely positioned, so the moment that root has a
    // height the legend lays itself out at the top of the box, over the plot.
    // The dots carry their own names (`showInlineLabels` defaults on) and each
    // name already leads with the study's item letter, so nothing is lost.
    <div
      data-testid="summary-quadrant"
      className="w-full min-w-0"
      style={{ height: 'clamp(320px, 32vw, 420px)' }}
    >
      <QuadrantPlot
        data={data}
        cornerLabels={cornerLabels}
        shownOfTotalTemplate={t('studyReport.quadrantShownOfTotal')}
        testId="summary-quadrant-plot"
      />
    </div>
  )
}

/**
 * The comparison card: the declared table or the diagnostics scorecard as a
 * heat grid (plots Q2 follow-up). Python stamps the `heat-grid` plot
 * (`comparison_grid_plot`, which leaves the same pooled-only rows off); a card
 * stored before it reads through `readComparisonCard`'s legacy reader (plain
 * grid, no heat). The rule below now lives in that reader.
 *
 * PER VERSION, NEVER ONE POOLED NUMBER (Fernando 2026-09-08). A scorecard can
 * carry rows measured once for the whole sample alongside rows split per tested
 * version -- a question asked before either ad was shown has one score and no
 * per-ad breakdown. Once the study has versions to compare, that pooled row has
 * nothing per version to say: its score used to land in an `__overall__` cell
 * that is not one of the columns, so the row rendered as a line of dashes. It
 * is dropped instead.
 *
 * The single `__overall__` column survives only for a study with no versions at
 * all, where the pooled score IS the read.
 *
 * THE DROP IS DISCLOSED, not silent (L16). A measure that was fielded, scored
 * and then left off the card because it has no per-version breakdown is a read
 * the study paid for and the reader cannot see, and a grid that simply gets
 * shorter says nothing. The names come back as `droppedMeasures` and the view
 * prints them under the grid. Named here rather than on the analysis payload
 * because the decision is made HERE: the same scorecard drawn for a study with
 * no versions keeps every one of these rows, so no field on the Python side
 * could state which rows a given rendering dropped.
 */
function GridView(props: { payload: GridPayload; itemTags: Record<string, string> }) {
  const { payload, itemTags } = props
  const { t, i18n } = useTranslation('app')

  const read = readComparisonCard(payload as unknown as StoredComparisonCard, itemTags)
  // A card stored before the stamp prints its names as today's grid did
  // (`formatLabel` on every row and column name); stamped names print as Python wrote them.
  const data = read.isStamped
    ? read.payload.data
    : {
        ...read.payload.data,
        rows: read.payload.data.rows.map((row) => ({ ...row, label: formatLabel(row.label) })),
        columns: read.payload.data.columns.map((column) => ({ ...column, label: formatLabel(column.label) })),
      }

  const grid =
    data.rows.length === 0 || data.columns.length === 0 ? (
      <Text size="sm" className="text-muted-foreground" data-testid="summary-grid-empty">
        {t('studyReport.noGridData')}
      </Text>
    ) : (
      <HeatGridPlotView
        plot={{ ...read.payload, data, props: { locale: i18n.language } } as unknown as HeatGridPlotViewProps['plot']}
        formatCellValue={read.formatValue}
        emptyCellText={read.emptyCellText}
        testIdPrefix="summary-grid-plot"
      />
    )
  return (
    <div className="flex flex-col gap-2">
      {grid}
      {read.droppedMeasures.length > 0 && (
        <Text
          size="xs"
          className="text-muted-foreground"
          data-testid="summary-grid-pooled-only"
        >
          {t('studyReport.gridPooledOnly', { measures: read.droppedMeasures.join(', ') })}
        </Text>
      )}
    </div>
  )
}

function DistributionView(props: { payload: DistributionPayload }) {
  const { payload } = props
  const { t, i18n } = useTranslation('app')
  // Plots plan J: a card built since J carries Python's `choice-bars` plot
  // (with the share-donut alternative when a donut can draw it) and draws it
  // in the viewer's UI language (decision 5). A card stored before J reads
  // through the legacy adapter: its rows as stored, today's figures (the
  // stored `unit` suffix rule) and labels, no winner.
  const read = readDistributionCard(payload as unknown as StoredDistributionCard)
  if (!read.isStamped && payload.rows.length === 0) {
    return (
      <Text size="sm" className="text-muted-foreground" data-testid="summary-distribution-empty">
        {t('studyReport.noDistributionData')}
      </Text>
    )
  }
  return (
    <ChoicePlotView
      data={read.payload.data}
      donut={donutAlternative(read.alternatives)}
      // A card stored before J highlights nothing: every bar keeps today's primary colour.
      highlight={read.isStamped ? read.payload.props?.highlight : 'none'}
      note={payload.unitLabel ?? null}
      toggleLabels={{ bars: t('studyReport.plotToggle.bars'), donut: t('studyReport.plotToggle.donut') }}
      otherToggleLabels={{ expand: (count: number) => t('studyReport.plotOther.expand', { count }), collapse: t('studyReport.plotOther.collapse') }}
      locale={i18n.language}
      formatShare={read.formatShare}
      formatItemLabel={read.isStamped ? undefined : formatLabel}
      testIdPrefix="summary-distribution-plot"
    />
  )
}

function PriceCurveView(props: { payload: PriceCurvePayload }) {
  const { payload } = props
  const { curve } = payload
  const { t } = useTranslation('app')
  const plotLabels = usePriceCurvesLabels()
  // The base line under the chart, one whole translated sentence per shape --
  // never a translated fragment glued to a second one (D5c): "43 of 100
  // respondents gave four consistent prices" is never "asked", and when a
  // reference spend exists its own base rides in the SAME sentence rather
  // than a concatenated clause, so every locale controls its own word order.
  //
  // The count is `baseSize`, NOT `completeCount`: `build_price_curve` takes
  // `completeCount` BEFORE the ordering filter and `baseSize` after it, and the
  // price sensitivity slide's `_base_line` prints `baseSize` under the
  // identical sentence (`usePriceCurveBaseLine`).
  const baseLine = usePriceCurveBaseLine(curve)
  // Plots plan G: a curve carrying `reported` and `displayWindow` draws as the
  // `price-curves` plot, aligned to the price sensitivity slide, with the
  // chrome in the viewer's UI language. An older stored curve keeps its
  // legacy reader.
  if (summaryCardPriceCurveAdapter.accepts(payload)) {
    const { data } = summaryCardPriceCurveAdapter.read(payload)
    return (
      <PriceCurvesPlot
        data={{ ...data, labels: plotLabels }}
        props={{ size: 'card', fit: 'card' }}
        baseLine={baseLine}
        testIdPrefix="summary-price-curves"
      />
    )
  }
  return (
    <PriceCurveChart
      curve={curve}
      baseLine={baseLine}
      labels={{
        priceSensitivity: t('studyReport.priceSensitivity'),
        optimalPricePoint: t('studyReport.optimalPricePoint'),
        acceptableRange: t('studyReport.acceptableRange'),
        rangeSeparator: t('studyReport.priceRangeSeparator'),
        noCurrency: t('studyReport.noCurrency'),
        lowerAcceptablePrice: t('studyReport.lowerAcceptablePrice'),
        upperAcceptablePrice: t('studyReport.upperAcceptablePrice'),
        currentSpend: t('studyReport.typicalCurrentSpend'),
      }}
    />
  )
}

function surveyQuestionTitle(survey: any, questionId: string): string {
  const question = findSurveyQuestion(survey, questionId)
  const title = question?.title ?? question?.name ?? ''
  return String(title).trim()
}

/**
 * The question texts one card should print above its figures, in the order
 * analysis read them.
 *
 * The stamped text first: it is the question's own title as analysis read it
 * off the draft the numbers came from, already cleaned of `@`-mentions, and it
 * is present in every context the card renders in (a report with no survey
 * loaded, an export, the golden gallery). The compiled survey is the fallback
 * for a card that names its question without carrying its wording.
 *
 * Nothing is inferred. A card that names no question prints no heading, which
 * is why this can return an empty list.
 */
function summaryCardQuestionTexts(
  sourceQuestions: Array<SummaryCardQuestion>,
  survey: any,
): Array<string> {
  const texts: Array<string> = []

  for (const source of sourceQuestions) {
    const stamped = (source.questionText ?? '').trim()
    const text = stamped || surveyQuestionTitle(survey, source.questionId)
    if (!text) {
      continue
    }
    if (!texts.includes(text)) {
      texts.push(text)
    }
  }

  return texts
}

/**
 * The respondents behind ONE card, or null when its payload states no honest
 * single number.
 *
 * Read from the payload here rather than at the frame, because whose base a
 * card has is a property of its shape. The two ranking families and the
 * distribution are stamped by their producer (`baseSize`); a lift or a grid is
 * several measures side by side, so its base is the SMALLEST of them, the same
 * rule `KpiListView`'s `exhibitBaseSize` applies: a frame reading n=200 over a
 * row measured on 12 respondents overstates the exhibit.
 *
 * The two Insights shapes are deliberately not here. `kpi_rows` and `scorecard`
 * already print their own base inside the card (`summary-kpi-base`,
 * `summary-scorecard-base`) and a frame line would print it a second time.
 */
export function summaryCardBaseSize(
  card: SummaryCard,
  source: MetricFigureSource,
): number | null {
  const payload = card.payload
  if (!payload) {
    return null
  }

  function metricBase(metricId?: string | null): number | null {
    if (!metricId) {
      return null
    }
    const figure = resolveMetricFigure(source, metricId)
    return figure != null && figure.baseSize > 0 ? figure.baseSize : null
  }

  function smallest(values: Array<number | null | undefined>): number | null {
    const usable = values.filter(
      (value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0,
    )
    return usable.length > 0 ? Math.min(...usable) : null
  }

  switch (payload.shape) {
    case 'podium':
    case 'per_item_rank':
    case 'dim_rank':
    case 'distribution':
      return smallest([payload.baseSize])
    case 'lift': {
      // Plots plan F: Python stamps the frame's base on the payload. What
      // follows is the legacy read for lift cards stored before the stamp.
      if (payload.baseSize != null) {
        return smallest([payload.baseSize])
      }
      // The fewest pairs any shown figure rests on, as Python counted them per
      // grid row and per lane; the parent metric's base (people paired on at
      // least one row) only when no row states its pairs.
      const card = withoutPooledGridRows(payload.scorecard, source.survey)
      if (!card) {
        return null
      }
      const pairs = smallest(
        card.rows.flatMap((row) => [
          row.pairs,
          ...Object.values(row.byAssetId ?? {}).map((score) => score.pairs),
        ]),
      )
      return pairs ?? smallest(card.rows.map((row) => metricBase(row.metricId)))
    }
    case 'comparison': {
      // Plots plan F: the stamped base first; the reads below are the legacy
      // reader for grid cards stored before it.
      if (payload.baseSize != null) {
        return smallest([payload.baseSize])
      }
      if (payload.table) {
        return smallest(payload.table.columns.map((column) => column.baseSize))
      }
      if (payload.scorecard) {
        return smallest(payload.scorecard.rows.map((row) => metricBase(row.metricId)))
      }
      return null
    }
    default:
      return null
  }
}

export interface SummaryCardBaseProps {
  card: SummaryCard
  context: SummaryCardRenderContext
  /** Whether the frame already prints this card's question above the body. */
  withQuestions: boolean
}

/**
 * The base the card was measured on, and the control that says which reads it
 * came from.
 *
 * ON THE FRAME, NOT IN THE CHART. Every Featured shape had the same gap: a
 * podium, a map, a lift table and a grid all state figures and none of them
 * said how many respondents are behind them, and each one would have had to
 * grow its own footer to say so. The frame owns the chrome (the slot name, the
 * question, and now the base), the body owns the figures.
 *
 * The info control carries the reads only when the frame could NOT print them
 * above: a card sourcing two or more questions prints no heading, because a
 * single caption would mislabel the rest, and that is exactly the card whose
 * reader has no way to see what was asked. One question, already printed
 * overhead: no control, because a tooltip repeating the line above it is
 * noise.
 */
export function SummaryCardBase(props: SummaryCardBaseProps) {
  const { card, context, withQuestions } = props
  const { t } = useTranslation('app')
  const [open, setOpen] = useState(false)

  const source: MetricFigureSource = context.canonicalAnalysis
    ? { ...context.canonicalAnalysis, survey: context.survey }
    : { survey: context.survey }
  const baseSize = summaryCardBaseSize(card, source)
  // Python's mark on the frame's base (`SummaryCard.lowBase`), every card kind alike.
  const lowBase = card.lowBase === true

  const texts = withQuestions
    ? []
    : summaryCardQuestionTexts(card.sourceQuestions ?? [], context.survey)

  function handlePointerDown(event: { preventDefault: () => void }) {
    event.preventDefault()
  }

  function handleClick(event: { preventDefault: () => void }) {
    event.preventDefault()
    setOpen((current) => !current)
  }

  if (baseSize == null) {
    return null
  }

  return (
    <div
      className="flex items-center justify-end gap-1"
      data-testid={`summary-card-base-${card.slot}`}
    >
      <Text
        size="sm"
        className="text-[10px] text-muted-foreground tabular-nums"
        data-testid={`summary-card-base-figure-${card.slot}`}
      >
        {lowBase
          ? `${t('studyReport.scorecardBase', { n: baseSize })}. ${t('studyReport.numericChart.lowBase')}`
          : t('studyReport.scorecardBase', { n: baseSize })}
      </Text>
      {texts.length > 0 && (
        <TooltipProvider delayDuration={150}>
          <Tooltip open={open} onOpenChange={setOpen}>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid={`summary-card-base-info-${card.slot}`}
                aria-label={t('studyReport.kpiFigureInfo')}
                aria-expanded={open}
                onPointerDown={handlePointerDown}
                onClick={handleClick}
                className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
              >
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px] space-y-2 p-3 text-left">
              {texts.map((text, idx) => (
                <Text
                  key={idx}
                  size="sm"
                  className="leading-snug"
                  data-testid={`summary-card-base-info-question-${card.slot}-${idx}`}
                >
                  {text}
                </Text>
              ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  )
}

export interface SummaryCardQuestionsProps {
  card: SummaryCard
  context: SummaryCardRenderContext
}

/**
 * The question a card's figures answer, printed directly above them.
 *
 * EVERY CARD THAT SHOWS AN ANSWER SHOWS ITS QUESTION (Fernando 2026-09-12).
 * Bars labelled "More than once a week 56%" under a caption reading "Share"
 * are a chart a reader cannot interpret: the labels name the options and the
 * unit names the scale, and neither says what was asked. So the heading is
 * card chrome rather than one shape's decoration, drawn from the card contract
 * for whichever slot holds it, with no branch on shape, study or instrument.
 *
 * The `fallback` shape carries no source questions on purpose: that card draws
 * the question itself through the Survey Data tab's own card, and a heading
 * here would print it twice.
 */
export function SummaryCardQuestions(props: SummaryCardQuestionsProps) {
  const { card, context } = props

  const texts = summaryCardQuestionTexts(card.sourceQuestions ?? [], context.survey)

  if (texts.length === 0) {
    return null
  }

  return (
    <div
      className="flex flex-col gap-1 min-w-0"
      data-testid={`summary-card-question-${card.slot}`}
    >
      {texts.map((text, idx) => (
        <Text
          key={idx}
          size="sm"
          className="font-medium text-foreground"
          data-testid={`summary-card-question-${card.slot}-${idx}`}
        >
          {text}
        </Text>
      ))}
    </div>
  )
}

function FallbackView(props: { payload: FallbackPayload; context: SummaryCardRenderContext }) {
  const { payload, context } = props
  const { validResponses, segmentableQuestions, assetMentionDisplayMap } = useReportQuestionCards({
    survey: context.survey,
    responses: context.responses,
    segmentationMappings: context.segmentationMappings,
    monadicLaneScope: context.canonicalAnalysis?.monadicLaneScope,
  })
  const rawQuestion = findSurveyQuestion(context.survey, payload.questionId)
  if (!rawQuestion) {
    // The card is `available` and its question is not in the compiled survey:
    // an empty frame with a slot name over it reads as a card still loading.
    // Say the same sentence the frame says for a read that never landed.
    return <UnresolvedQuestionNotice />
  }
  const questionMap = context.survey?._canonicalConfig?.questionMap
  // Same normalisation the Survey Data tab runs before it hands a question to
  // `QuestionCard` (`ReportDashboard`): without it the fallback prints the raw
  // "@concept_a" mention instead of the asset's display title.
  const question = normalizeQuestionAssetMentions(rawQuestion, assetMentionDisplayMap)
  // No stamped partner to pass: `FallbackPayload` carries the question id and a
  // diagnostic and nothing else on purpose, so the resolver infers here. The
  // FEATURED question card (`FeaturedQuestionView`) is the one that gets
  // analysis's own answer.
  const pairedQuestion = resolvePairedQuestion(context.survey, questionMap, payload.questionId)

  return (
    <div data-testid="summary-fallback-question">
      <QuestionCard
        question={question}
        pairedQuestion={pairedQuestion}
        segmentableQuestions={segmentableQuestions}
        responses={validResponses}
        populationAttributes={context.analysisColumns}
        canonicalAnalysis={context.canonicalAnalysis}
        canonicalQuestionMap={questionMap}
        assetContextItems={context.survey?._canonicalConfig?.assetContext?.items}
        testedAssets={readTestedAssets(context.survey)}
        layout="stacked"
        segmentationMappings={context.segmentationMappings}
      />
    </div>
  )
}

function speakerInitialsFrom(name: string, stamped?: string | null): string {
  if (stamped) return stamped
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
}

function SummaryQuoteStackAvatar(props: { idx: number; imageUrl: string }) {
  const { idx, imageUrl } = props
  const { displayUrl, handleImageError } = usePersonaThumbFallback(imageUrl, 64)

  return (
    <img
      src={displayUrl}
      alt=""
      data-testid={`summary-quote-avatar-${idx}`}
      className="w-7 h-7 rounded-full object-cover border-2 border-card-inset"
      style={{ marginLeft: idx === 0 ? 0 : -10 }}
      onError={() => handleImageError()}
    />
  )
}

interface QuotesViewProps {
  payload: QuotesPayload
  audienceProfiles?: Array<SummaryAudienceProfile>
  responses?: Array<any>
  respondentCount?: number
  onTwinChatClick?: (respondentId: string) => void
  survey?: any
  canonicalAnalysis?: CanonicalAnalysisOutput | null
}

/**
 * Compiled SurveyJS name for a canonical question id. `laneQuestionNames`
 * stores those compiled names, so comparing `questionId` to the list would
 * miss every lane question. No name in the map means the question is not
 * treated as a lane question.
 */
function surveyJsNameForQuestion(survey: unknown, questionId: string | null | undefined): string | null {
  if (!questionId) {
    return null
  }
  const questionMap = (survey as { _canonicalConfig?: { questionMap?: Record<string, { questionId?: string; surveyJsName?: string }> } } | undefined)
    ?._canonicalConfig?.questionMap
  if (!questionMap) {
    return null
  }
  const byKey = questionMap[questionId]
  if (byKey?.surveyJsName) {
    return byKey.surveyJsName
  }
  const fromValues = Object.values(questionMap).find((entry) => entry.questionId === questionId)
  return fromValues?.surveyJsName ?? null
}

/** Display name of the version this quote is about, or null when the chip stays off. */
function quoteVersionChipLabel(
  quote: { assetId?: string | null; questionId?: string | null } | undefined,
  canonicalAnalysis: CanonicalAnalysisOutput | null | undefined,
  survey: unknown,
): string | null {
  const scope = canonicalAnalysis?.monadicLaneScope
  if (!scope || (scope.laneAssetIds ?? []).length < 2 || !quote?.assetId) {
    return null
  }
  const surveyName = surveyJsNameForQuestion(survey, quote.questionId)
  if (!surveyName || !(scope.laneQuestionNames ?? []).includes(surveyName)) {
    return null
  }
  const label = canonicalAnalysis?.assetLabels?.[quote.assetId]
  return label || null
}

/** The speaker's photo, or the plot's fallback when there is none (or it fails to load). */
function SummaryQuoteSpeakerAvatar(props: { imageUrl?: string; name: string; fallback: ReactNode }) {
  const { imageUrl, name, fallback } = props

  const { displayUrl, handleImageError } = usePersonaThumbFallback(imageUrl, 64)

  if (!displayUrl) {
    return <>{fallback}</>
  }
  return (
    <img
      src={displayUrl}
      alt={name}
      data-testid="summary-quote-speaker-avatar"
      className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-border/40"
      onError={() => handleImageError()}
    />
  )
}

/**
 * The summary quotes card: a thin host around the `quote-list` plot in its
 * carousel layout (plots plan S). The host owns what analysis output never
 * stores: the audience avatar stack, the speaker's image (joined by
 * respondent id; by name only for a card stored before S), the version chip,
 * the chat button and the app-language words of the arrows and dots.
 */
function QuotesView(props: QuotesViewProps) {
  const { payload, audienceProfiles, responses, respondentCount, onTwinChatClick, survey, canonicalAnalysis } = props

  const { t } = useTranslation('app')

  const { plot, isLegacy } = useMemo(() => readQuotesCard(payload), [payload])

  const imageById = useMemo(
    () => buildRespondentImageMapById([...(audienceProfiles ?? []), ...(responses ?? [])]),
    [audienceProfiles, responses],
  )

  // Legacy only: a card stored before S joins the image by the speaker's name.
  const profileImageByName = useMemo(
    () =>
      buildResponseImageMap(
        (audienceProfiles ?? []).map((profile) => ({
          image_url: profile.image_url,
          respondent_attributes: { name: profile.name },
        })),
      ),
    [audienceProfiles],
  )

  const stackProfiles = useMemo(() => {
    const profiles = audienceProfiles ?? []
    return [
      ...profiles.filter((profile) => !!profile.image_url),
      ...profiles.filter((profile) => !profile.image_url),
    ].slice(0, AVATAR_VISIBLE)
  }, [audienceProfiles])

  const extraCount = Math.max(
    0,
    (respondentCount ?? audienceProfiles?.length ?? 0) - stackProfiles.length,
  )

  const navLabels = useMemo(
    () => ({
      prev: t('studyReport.quotePrev'),
      next: t('studyReport.quoteNext'),
      goTo: (position: number) => t('studyReport.quoteGoTo', { number: position }),
    }),
    [t],
  )

  const onSpeakerImage = (speaker: QuoteListSpeaker): string | undefined => {
    const byId = speaker.respondentId ? imageById.get(speaker.respondentId) : undefined
    if (byId || !isLegacy) {
      return byId
    }
    return speaker.name ? profileImageByName.get(normalizePersonaMapKey(speaker.name)) : undefined
  }

  // The same resolution the card heading runs: the stamped title first, the
  // compiled survey as the fallback for a quote that names its question
  // without carrying the wording.
  const questionTextOf = (quote: QuoteListQuote) =>
    summaryCardQuestionTexts(
      [{ questionId: quote.questionId ?? '', questionText: quote.questionText ?? '' }],
      survey,
    )[0]

  if (plot.data.quotes.length === 0) return null

  const header =
    stackProfiles.length > 0 ? (
      <div className="flex items-center" data-testid="summary-quote-avatar-stack">
        {stackProfiles.map((profile, idx) =>
          profile.image_url ? (
            <SummaryQuoteStackAvatar key={idx} idx={idx} imageUrl={profile.image_url} />
          ) : (
            <span
              key={idx}
              data-testid={`summary-quote-avatar-${idx}`}
              className="inline-flex"
              style={{ marginLeft: idx === 0 ? 0 : -10 }}
            >
              <UserAvatar
                initials={speakerInitialsFrom(profile.name ?? '')}
                name={profile.name}
                size="xs"
              />
            </span>
          ),
        )}
        {extraCount > 0 && (
          <span
            className="w-7 h-7 rounded-full bg-muted border-2 border-card-inset flex items-center justify-center text-[9px] font-semibold text-muted-foreground"
            style={{ marginLeft: -10 }}
          >
            +{extraCount}
          </span>
        )}
      </div>
    ) : null

  return (
    <QuoteListPlot
      data={plot.data}
      size="card"
      layout="carousel"
      header={header}
      navLabels={navLabels}
      questionTextOf={questionTextOf}
      renderSpeakerAvatar={({ speaker, name, fallback }) => (
        <SummaryQuoteSpeakerAvatar imageUrl={onSpeakerImage(speaker)} name={name} fallback={fallback} />
      )}
      renderSpeakerActions={(quote, name) => {
        const versionChipLabel = quoteVersionChipLabel(quote, canonicalAnalysis, survey)
        // A chat opens by the speaker's respondent id, never by the name shown:
        // two twins can share a name. A speaker with no id has no chat button.
        const respondentId = quote.speaker?.respondentId
        return (
          <>
            {versionChipLabel && (
              <SubsegmentChip label={versionChipLabel} testId="summary-quote-version-chip" />
            )}

            {onTwinChatClick && respondentId && (
              <button
                type="button"
                onClick={() => onTwinChatClick(respondentId)}
                data-testid="quote-chat-icon"
                className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--report-secondary-icon-bg)] flex items-center justify-center hover:opacity-90 transition-opacity"
                aria-label={name}
              >
                <MessageSquare className="w-3 h-3 text-[var(--report-secondary-icon-fg)]" />
              </button>
            )}
          </>
        )
      }}
    />
  )
}

function payloadOf<T extends SummaryCardPayload>(card: SummaryCard, shape: CardShape): T | null {
  const payload = card.payload
  if (!payload || payload.shape !== shape) return null
  return payload as T
}

/**
 * The app-surface wrapper for a deck-ui chart.
 *
 * deck-ui draws with `--slide-*` tokens, and the report page never loads
 * `@digital-twins/deck-ui`'s `tokens.css` (only `SlideFrame` imports it). So a
 * chart drawn here reads a half-populated token set: the ones
 * `applyThemeBrandTokens` writes app-wide are present and brand-tinted, and
 * the rest fall through to the hardcoded light hexes each component carries.
 * `.chart-app-surface` (globals.css) is the existing remedy, already used by
 * `quick-study-results/PlotRenderer`: inside `.dark` it remaps the on-surface
 * label tokens to the theme-aware ones.
 *
 * Applied to every card that can put a deck-ui chart on screen. That is the
 * deck-ui charts drawn here directly AND the two question cards (`question`,
 * `fallback`): `QuestionCard` draws its own chart, so the surface has to be
 * around it too. (Supersedes the note that stood here, which excluded the D8
 * question fallback on the grounds that it is an app component: the wrapper is
 * an app component, the chart inside it is not.) The KPI list and the quotes
 * carousel draw no chart and stay outside.
 */
function ChartSurface(props: { children: ReactNode }) {
  return <div className="chart-app-surface">{props.children}</div>
}

/**
 * Whether an available card still has a figure to draw once withheld figures
 * are dropped. A lift stored before grid rows were paired one by one is never
 * shown (`withoutPooledGridRows`); when that leaves no row, `renderSummaryCard`
 * draws nothing and the slot says it is unavailable instead of framing an
 * empty body.
 */
export function summaryCardHasFigures(card: SummaryCard, context: SummaryCardRenderContext): boolean {
  if (card.shape !== 'lift') {
    return true
  }
  const payload = payloadOf<LiftPayload>(card, 'lift')
  return payload != null && withoutPooledGridRows(payload.scorecard, context.survey) != null
}

export function renderSummaryCard(
  card: SummaryCard,
  context: SummaryCardRenderContext,
): ReactNode {
  if (
    card.status !== 'available' ||
    !card.payload ||
    SUMMARY_CARD_RENDERER_BRANCHES[card.shape] === null
  )
    return null

  switch (card.shape) {
    case 'kpi_rows': {
      const payload = payloadOf<KpiListPayload>(card, 'kpi_rows')
      return payload ? (
        <KpiListView
          payload={payload}
          canonicalAnalysis={context.canonicalAnalysis}
          survey={context.survey}
        />
      ) : null
    }
    case 'lift': {
      const payload = payloadOf<LiftPayload>(card, 'lift')
      // A lift stored before grid rows were paired one by one is never shown.
      const scorecard = payload ? withoutPooledGridRows(payload.scorecard, context.survey) : null
      return scorecard ? <LiftView scorecard={scorecard} itemTags={context.itemTags} /> : null
    }
    case 'podium':
    case 'per_item_rank':
    case 'dim_rank': {
      const payload = payloadOf<RankingPayload>(card, card.shape)
      return payload ? (
        <ChartSurface>
          <RankingView payload={payload} itemTags={context.itemTags} />
        </ChartSurface>
      ) : null
    }
    case 'quadrant': {
      const payload = payloadOf<QuadrantPayload>(card, 'quadrant')
      return payload ? (
        <ChartSurface>
          <QuadrantView payload={payload} itemTags={context.itemTags} />
        </ChartSurface>
      ) : null
    }
    case 'comparison': {
      const payload = payloadOf<GridPayload>(card, 'comparison')
      return payload ? (
        <ChartSurface>
          <GridView payload={payload} itemTags={context.itemTags} />
        </ChartSurface>
      ) : null
    }
    case 'distribution': {
      const payload = payloadOf<DistributionPayload>(card, 'distribution')
      return payload ? (
        <ChartSurface>
          <DistributionView payload={payload} />
        </ChartSurface>
      ) : null
    }
    case 'price_curve': {
      const payload = payloadOf<PriceCurvePayload>(card, 'price_curve')
      return payload ? (
        <ChartSurface>
          <PriceCurveView payload={payload} />
        </ChartSurface>
      ) : null
    }
    case 'quotes': {
      const payload = payloadOf<QuotesPayload>(card, 'quotes')
      return payload ? (
        <QuotesView
          payload={payload}
          audienceProfiles={context.audienceProfiles}
          responses={context.responses}
          respondentCount={context.respondentCount}
          onTwinChatClick={context.onTwinChatClick}
          survey={context.survey}
          canonicalAnalysis={context.canonicalAnalysis}
        />
      ) : null
    }
    case 'fallback': {
      const payload = payloadOf<FallbackPayload>(card, 'fallback')
      return payload ? (
        <ChartSurface>
          <FallbackView payload={payload} context={context} />
        </ChartSurface>
      ) : null
    }
    case 'question': {
      const payload = payloadOf<QuestionPayload>(card, 'question')
      return payload ? (
        <ChartSurface>
          <FeaturedQuestionView payload={payload} context={context} />
        </ChartSurface>
      ) : null
    }
    case 'scorecard': {
      const payload = payloadOf<ScorecardPayload>(card, 'scorecard')
      return payload ? (
        <SummaryScorecard
          payload={payload}
          analysis={context.canonicalAnalysis}
          survey={context.survey}
          itemTags={context.itemTags}
        />
      ) : null
    }
    case 'demand_curve':
      // Reserved wire value. Current accepted plans reject it until a producer,
      // payload member and renderer are added to the shared capability table.
      return null
    default: {
      const exhaustive: never = card.shape
      return exhaustive
    }
  }
}

